import axios from 'axios';
import { isPublicPath } from '../utils/publicPaths';
import { participantApiBase } from '../utils/sandboxEndpoints';

// Create axios instance with default config
const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Clears the local session and bounces to the login page when the backend
// rejects our session token (401). Public pages stay where they are: on the
// sign-in pages a 401 is just a failed attempt, and the emailed and QR-code
// pages must keep their one-time token.
function handleSessionExpiry() {
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  localStorage.removeItem('tenantId');
  if (!isPublicPath()) {
    window.location.href = '/login';
  }
}

// Add request interceptor for logging + tenant context injection
api.interceptors.request.use(
  (config) => {
    console.log(`[API Request] ${config.method.toUpperCase()} ${config.url}`);
    // Attach the session token issued at login. The backend derives BOTH
    // authentication and the tenant from this verified token — the browser no
    // longer sends a shared X-API-Key or a client-controlled X-Tenant-Id.
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`[API Response] ${response.config.url}`, response.status);
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      handleSessionExpiry();
    }
    console.error('[API Response Error]', error.response || error);
    return Promise.reject(error);
  }
);

/**
 * Dashboard API Service
 */
export const dashboardApi = {
  /**
   * Get comprehensive statistics
   */
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  /**
   * Get statistics by status
   */
  getStatsByStatus: async () => {
    const response = await api.get('/admin/stats/by-status');
    return response.data;
  },

  /**
   * Get volume statistics
   * @param {string} period - Time period (hourly, daily, weekly)
   * @param {number} limit - Number of data points
   */
  getVolumeStats: async (period = 'hourly', limit = 24) => {
    const response = await api.get('/admin/stats/volume', {
      params: { period, limit }
    });
    return response.data;
  },

  /**
   * Get filtered transactions
   * @param {Object} filters - Filter parameters
   */
  getTransactions: async (filters = {}) => {
    const response = await api.get('/admin/transactions', {
      params: filters
    });
    return response.data;
  },

  /**
   * Get paginated transactions — preferred over getTransactions for production.
   * Uses server-side pagination and batch status enrichment (2 DB queries per page).
   *
   * @param {Object} params
   * @param {number} [params.page=0]       0-indexed page number
   * @param {number} [params.size=20]      rows per page (max 100)
   * @param {string} [params.direction]    'inbound' | 'outbound'
   * @param {string} [params.search]       free-text search on endToEndId / txId / grpMsgId
   * @returns {Promise<PageResponseDto>}   { content, page, size, totalElements, totalPages, ... }
   */
  getTransactionsPaged: async ({ page = 0, size = 20, direction, search } = {}) => {
    const params = { page, size };
    if (direction) params.direction = direction;
    if (search)    params.search    = search;
    const response = await api.get('/admin/transactions/page', { params });
    return response.data;
  },

  /**
   * Get recent transactions
   * @param {number} limit - Number of transactions
   */
  getRecentTransactions: async (limit = 10) => {
    const response = await api.get('/admin/transactions/recent', {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Get transaction by ID
   * @param {number} id - Transaction ID
   */
  getTransactionById: async (id) => {
    const response = await api.get(`/admin/transactions/${id}`);
    return response.data;
  },

  /**
   * Health check
   */
  healthCheck: async () => {
    const response = await api.get('/admin/health');
    return response.data;
  },

  // =========================================================================
  // Request-to-Pay (pain.013) API Methods
  // =========================================================================

  /**
   * Get pain.013 RtP statistics
   * @returns {Promise<Object>} RtP statistics
   */
  getRtpStats: async () => {
    const response = await api.get('/admin/rtp/stats');
    return response.data;
  },

  /**
   * Get list of RtP transactions with filtering
   * @param {Object} filters - Filter parameters
   * @param {number} filters.limit - Maximum results (default: 100)
   * @param {string} filters.status - Status filter (PENDING, APPROVED, REJECTED, etc.)
   * @param {string} filters.direction - Direction filter (OUTGOING, INCOMING)
   * @param {string} filters.startDate - Start date (YYYY-MM-DD)
   * @param {string} filters.endDate - End date (YYYY-MM-DD)
   * @param {string} filters.search - Search term (msgId, endToEndId, party names)
   * @returns {Promise<Array>} List of RtP transactions
   */
  getRtpTransactions: async (filters = {}) => {
    const params = {};
    
    if (filters.limit) params.limit = filters.limit;
    if (filters.status) params.status = filters.status;
    if (filters.direction) params.direction = filters.direction;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.search) params.search = filters.search;
    
    const response = await api.get('/admin/rtp/transactions', { params });
    return response.data;
  },

  /**
   * Get a specific RtP transaction by ID
   * @param {number} id - RtP transaction ID
   * @returns {Promise<Object>} RtP transaction details
   */
  getRtpTransactionById: async (id) => {
    const response = await api.get(`/admin/rtp/transactions/${id}`);
    return response.data;
  },

  /**
   * Get related messages for an RtP transaction
   * Returns linked pain.013, pacs.008, and pacs.002 messages by EndToEndId
   * @param {number} id - RtP transaction ID
   * @returns {Promise<Object>} Related messages (rtpRequest, payment, statusUpdates)
   */
  getRtpRelatedMessages: async (id) => {
    try {
      const response = await api.get(`/admin/rtp/transactions/${id}/related`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return { rtpRequest: null, payment: null, statusUpdates: [] };
      }
      throw error;
    }
  },

  /**
   * Get RtP status breakdown
   * @returns {Promise<Object>} Status counts
   */
  getRtpStatusBreakdown: async () => {
    const response = await api.get('/admin/rtp/status-breakdown');
    return response.data;
  },

  /**
   * Get RtP volume over time
   * @param {string} period - Time period (hour, day, week, month)
   * @param {number} days - Number of days to look back (optional)
   * @returns {Promise<Array>} Time-series data
   */
  getRtpVolume: async (period = 'day', days = null) => {
    const params = { period };
    if (days) params.days = days;
    
    const response = await api.get('/admin/rtp/volume', { params });
    return response.data;
  },

  /**
   * Search RtP transactions by EndToEndId
   * @param {string} endToEndId - End-to-End ID to search
   * @returns {Promise<Array>} Matching RtP transactions
   */
  searchRtpByEndToEndId: async (endToEndId) => {
    const response = await api.get(`/admin/rtp/search/e2e/${encodeURIComponent(endToEndId)}`);
    return response.data;
  },

  /**
   * Get RtP transactions requiring attention
   * Returns transactions that need action:
   * - Pending approvals >24 hours old
   * - Failed requests in last 7 days
   * @returns {Promise<Array>} RtP transactions requiring attention
   */
  getRtpAttentionRequired: async () => {
    const response = await api.get('/admin/rtp/attention-required');
    return response.data;
  },

  // =========================================================================
  // Request-to-Pay Status/Response (pain.014) API
  // =========================================================================

  /**
   * Get pain.014 RTP Status statistics for dashboard
   * @returns {Promise<Object>} RTP Status statistics
   */
  async getRtpStatusStats() {
    const response = await api.get('/admin/rtp-status/stats');
    return response.data;
  },

  /**
   * Get list of pain.014 RTP Status transactions with filtering
   * @param {Object} params - Query parameters (limit, grpSts, txSts, direction, startDate, endDate, search)
   * @returns {Promise<Array>} List of RTP Status transactions
   */
  async getRtpStatusTransactions(params = {}) {
    const response = await api.get('/admin/rtp-status/transactions', { params });
    return response.data;
  },

  /**
   * Get a specific pain.014 RTP Status transaction by ID
   * @param {number} id - RTP Status transaction ID
   * @returns {Promise<Object>} RTP Status transaction details
   */
  async getRtpStatusTransactionById(id) {
    const response = await api.get(`/admin/rtp-status/transactions/${id}`);
    return response.data;
  },

  /**
   * Get related messages for a pain.014 RTP Status transaction
   * @param {number} id - RTP Status transaction ID
   * @returns {Promise<Object>} Related messages (rtpStatus, rtpRequest, payment, paymentStatuses)
   */
  async getRtpStatusRelatedMessages(id) {
    try {
      const response = await api.get(`/admin/rtp-status/transactions/${id}/related`);
      return response.data;
    } catch (error) {
      console.error('Error fetching RTP Status related messages:', error);
      return { rtpStatus: null, rtpRequest: null, payment: null, paymentStatuses: [] };
    }
  },

  /**
   * Get RTP Status breakdown by group status code
   * @returns {Promise<Object>} Status code counts
   */
  async getRtpStatusBreakdown() {
    const response = await api.get('/admin/rtp-status/status-breakdown');
    return response.data;
  },

  /**
   * Get RTP Status volume over time
   * @param {Object} params - Query parameters (period, limit)
   * @returns {Promise<Array>} Time-series volume data
   */
  async getRtpStatusVolume(params = {}) {
    const response = await api.get('/admin/rtp-status/volume', { params });
    return response.data;
  },

  /**
   * Search RTP Status messages by original EndToEndId
   * @param {string} endToEndId - Original End-to-End ID
   * @returns {Promise<Array>} List of matching RTP Status transactions
   */
  async searchRtpStatusByEndToEndId(endToEndId) {
    const response = await api.get(`/admin/rtp-status/search/e2e/${encodeURIComponent(endToEndId)}`);
    return response.data;
  },

  /**
   * Get RTP Status messages requiring attention
   * @param {number} limit - Maximum results (default 50)
   * @returns {Promise<Array>} List of RTP Status transactions needing attention
   */
  async getRtpStatusAttentionRequired(limit = 50) {
    const response = await api.get('/admin/rtp-status/attention-required', {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Get pacs.028 status request statistics
   * @returns {Promise<Object>} Status request statistics
   */
  getStatusRequestStats: async () => {
    const response = await api.get('/admin/status-requests/stats');
    return response.data;
  },

  /**
   * Get list of status request messages with filtering
   * @param {Object} filters - Filter parameters
   * @param {number} filters.limit - Maximum results (default: 100)
   * @param {string} filters.status - Status filter (SIGNED, SENT, RECEIVED, PROCESSING, FAILED)
   * @param {string} filters.direction - Direction filter (OUTGOING_SIGNED, INCOMING)
   * @param {string} filters.startDate - Start date (YYYY-MM-DD)
   * @param {string} filters.endDate - End date (YYYY-MM-DD)
   * @param {string} filters.search - Search term (msgId, orglEndToEndId, orglTxId, agent names)
   * @returns {Promise<Array>} List of status request messages
   */
  getStatusRequests: async (filters = {}) => {
    const params = {};
    
    if (filters.limit) params.limit = filters.limit;
    if (filters.status) params.status = filters.status;
    if (filters.direction) params.direction = filters.direction;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.search) params.search = filters.search;
    
    const response = await api.get('/admin/status-requests', { params });
    return response.data;
  },

  /**
   * Get a specific status request by ID
   * @param {number} id - Status request ID
   * @returns {Promise<Object>} Status request details
   */
  getStatusRequestById: async (id) => {
    const response = await api.get(`/admin/status-requests/${id}`);
    return response.data;
  },

  /**
   * Get related messages for a status request
   * Returns linked pacs.028, pacs.008, and pacs.002 messages by orglEndToEndId
   * @param {number} id - Status request ID
   * @returns {Promise<Object>} Related messages (statusRequest, originalPayment, statusResponses)
   */
  getStatusRequestRelatedMessages: async (id) => {
    try {
      const response = await api.get(`/admin/status-requests/${id}/related`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return { statusRequest: null, originalPayment: null, statusResponses: [] };
      }
      throw error;
    }
  },

  /**
   * Get status request status breakdown
   * @returns {Promise<Object>} Status counts
   */
  getStatusRequestStatusBreakdown: async () => {
    const response = await api.get('/admin/status-requests/status-breakdown');
    return response.data;
  },

  /**
   * Get status request volume over time
   * @param {string} period - Time period (hour, day, week, month)
   * @param {number} days - Number of days to look back (optional)
   * @returns {Promise<Array>} Time-series data
   */
  getStatusRequestVolume: async (period = 'day', days = null) => {
    const params = { period };
    if (days) params.days = days;
    
    const response = await api.get('/admin/status-requests/volume', { params });
    return response.data;
  },

  /**
   * Search status requests by original end-to-end ID
   * @param {string} orglEndToEndId - Original end-to-end ID to search
   * @returns {Promise<Array>} Matching status requests
   */
  searchStatusRequestsByE2E: async (orglEndToEndId) => {
    const response = await api.get(`/admin/status-requests/search/e2e/${encodeURIComponent(orglEndToEndId)}`);
    return response.data;
  },

  /**
   * Get status requests requiring attention
   * Returns requests that need action:
   * - Requests with status SENT older than 1 hour
   * - Failed requests in last 7 days
   * @returns {Promise<Array>} Status requests requiring attention
   */
  getStatusRequestsAttentionRequired: async () => {
    const response = await api.get('/admin/status-requests/attention-required');
    return response.data;
  }

};

/**
 * Account Registration API Service
 */
export const registrationApi = {
  /**
   * Get registration statistics
   */
  getStats: async () => {
    const response = await api.get('/admin/registrations/stats');
    return response.data;
  },

  /**
   * Get filtered account registrations
   * @param {Object} filters - Filter parameters
   */
  getRegistrations: async (filters = {}) => {
    const response = await api.get('/admin/registrations', {
      params: filters
    });
    return response.data;
  },

  /**
   * Get recent registrations
   * @param {number} limit - Number of registrations
   */
  getRecentRegistrations: async (limit = 10) => {
    const response = await api.get('/admin/registrations/recent', {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Get registration by ID
   * @param {number} id - Registration ID
   */
  getRegistrationById: async (id) => {
    const response = await api.get(`/admin/registrations/${id}`);
    return response.data;
  }
};

/**
 * Account Modification API Service
 */
export const modificationApi = {
  /**
   * Get modification statistics
   */
  getStats: async () => {
    const response = await api.get('/admin/modifications/stats');
    return response.data;
  },

  /**
   * Get filtered account modifications
   * @param {Object} filters - Filter parameters
   */
  getModifications: async (filters = {}) => {
    const response = await api.get('/admin/modifications', {
      params: filters
    });
    return response.data;
  },

  /**
   * Get recent modifications
   * @param {number} limit - Number of modifications
   */
  getRecentModifications: async (limit = 10) => {
    const response = await api.get('/admin/modifications/recent', {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Get modification by ID
   * @param {number} id - Modification ID
   */
  getModificationById: async (id) => {
    const response = await api.get(`/admin/modifications/${id}`);
    return response.data;
  }
};

/**
 * Account Closure API Service
 */
export const closureApi = {
  /**
   * Get closure statistics
   */
  getStats: async () => {
    const response = await api.get('/admin/closures/stats');
    return response.data;
  },

  /**
   * Get filtered account closures
   * @param {Object} filters - Filter parameters
   */
  getClosures: async (filters = {}) => {
    const response = await api.get('/admin/closures', {
      params: filters
    });
    return response.data;
  },

  /**
   * Get recent closures
   * @param {number} limit - Number of closures
   */
  getRecentClosures: async (limit = 10) => {
    const response = await api.get('/admin/closures/recent', {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Get closure by ID
   * @param {number} id - Closure ID
   */
  getClosureById: async (id) => {
    const response = await api.get(`/admin/closures/${id}`);
    return response.data;
  }
};

/**
 * Message Generation API Service
 */
export const messageApi = {
  /**
   * List available schemas
   */
  listSchemas: async () => {
    const response = await api.get('/schemas');
    return response.data;
  },

  /**
   * Generate message
   */
  generateMessage: async (messageType, payload, format = 'xml', pretty = true) => {
    const response = await api.post(`/generate/${messageType}`, payload, {
      params: {
        format,
        pretty
      },
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  /**
   * Generate and sign message in one call
   */
  generateAndSignMessage: async (
    messageType,
    payload,
    format = 'xml',
    pretty = true,
    sign = true,
    forward = false) => {
      const response = await api.post(`/generate/${messageType}`, payload, {
        params: {
          format,
          pretty,
          sign,
          forward
        },
        headers: {
          'Content-Type': 'application/json'
        }
      });
      console.log(`[API Response] ${response.data}`);
      return response.data;
    },

  /**
   * Validate message
   */
  validateMessage: async (messageType, payload) => {
    const response = await api.post(`/validate/${messageType}`, payload);
    return response.data;
  }
};

/**
 * Sign/Verify API Service
 */
export const signingApi = {
  /**
   * Sign XML message
   */
  signMessage: async (xmlContent, pretty = true) => {
    const response = await api.post('/sign', xmlContent, {
      params: { pretty },
      headers: {
        'Content-Type': 'application/xml'
      }
    });
    return response.data;
  },

  /**
   * Verify signed message
   */
  verifyMessage: async (signedXml) => {
    const response = await api.post('/verify', signedXml, {
      headers: {
        'Content-Type': 'application/xml'
      }
    });
    return response.data;
  },

  /**
   * Health check
   */
  healthCheck: async () => {
    const response = await api.get('/healthz');
    return response.data;
  }
};

/**
 * Certificate API Service
 */
export const certificateApi = {
  /**
   * Get list of certificates with optional filtering
   * @param {Object} filters - Filter parameters
   * @param {string} filters.status - Status filter (ACTIVE, PENDING, REVOKED, EXPIRED)
   * @param {string} filters.type - Type filter (SELF_SIGNED, CA_SIGNED, IMPORTED)
   * @param {string} filters.search - Search term (alias, subject)
   * @returns {Promise<Array>} List of certificates
   */
  getCertificates: async (filters = {}) => {
    const params = {};
    
    if (filters.status) params.status = filters.status;
    if (filters.type) params.certificateType = filters.type;
    if (filters.search) params.search = filters.search;
    
    const response = await api.get('/certificates', { params });
    return response.data;
  },

  /**
   * Get certificate by ID
   * @param {number} id - Certificate ID
   * @returns {Promise<Object>} Certificate details
   */
  getCertificateById: async (id) => {
    const response = await api.get(`/certificates/${id}`);
    return response.data;
  },

  /**
   * Get certificate status summary
   * @returns {Promise<Object>} Status summary (counts, primary cert info, etc.)
   */
  getStatusSummary: async () => {
    const response = await api.get('/certificates/status/summary');
    return response.data;
  },

  /**
   * Get certificates expiring within specified days
   * @param {number} days - Number of days (default: 30)
   * @returns {Promise<Array>} List of expiring certificates
   */
  getExpiringCertificates: async (days = 30) => {
    const response = await api.get('/certificates/expiring', {
      params: { days }
    });
    return response.data;
  },

  /**
   * Get audit log for a certificate
   * @param {number} id - Certificate ID
   * @returns {Promise<Array>} Audit log entries
   */
  getAuditLog: async (id) => {
    const response = await api.get(`/certificates/${id}/audit`);
    return response.data;
  },

  /**
   * Generate self-signed certificate (Model 1)
   * @param {Object} request - Certificate generation request
   * @param {string} request.alias - Certificate alias
   * @param {string} request.commonName - Common Name (CN)
   * @param {string} request.organization - Organization (O)
   * @param {string} request.organizationalUnit - Organizational Unit (OU)
   * @param {string} request.locality - Locality (L)
   * @param {string} request.state - State/Province (ST)
   * @param {string} request.country - Country (C)
   * @param {string} request.keyAlgorithm - Key algorithm (RSA, DSA, EC)
   * @param {number} request.keySize - Key size in bits (2048, 3072, 4096)
   * @param {number} request.validityDays - Validity period in days
   * @returns {Promise<Object>} Generated certificate info
   */
  generateCertificate: async (request) => {
    const response = await api.post('/certificates/generate', request);
    return response.data;
  },

  /**
   * Generate Certificate Signing Request (Model 2 - Step 1)
   * @param {Object} request - CSR generation request (same fields as generateCertificate)
   * @returns {Promise<Object>} CSR generation result with csrPem, csrId, certificateId
   */
  generateCSR: async (request) => {
    const response = await api.post('/certificates/generate-csr', request);
    return response.data;
  },

  /**
   * Import CA-signed certificate (Model 2 - Step 2)
   * @param {number} csrId - CSR ID
   * @param {string} certificatePem - PEM-encoded CA-signed certificate
   * @param {string} caName - Certificate Authority name
   * @returns {Promise<Object>} Imported certificate info
   */
  importCACertificate: async (csrId, certificatePem, caName) => {
    const response = await api.post('/certificates/import-ca-certificate', {
      csrId,
      certificatePem,
      caName
    });
    return response.data;
  },

  /**
   * Activate certificate (make it primary)
   * @param {number} id - Certificate ID
   * @returns {Promise<void>}
   */
  activateCertificate: async (id) => {
    const response = await api.post(`/certificates/${id}/activate`);
    return response.data;
  },

  /**
   * Revoke certificate
   * @param {number} id - Certificate ID
   * @param {string} reason - Revocation reason
   * @returns {Promise<void>}
   */
  revokeCertificate: async (id, reason) => {
    const response = await api.post(`/certificates/${id}/revoke`, { reason });
    return response.data;
  },

  /**
   * Get CSR details by ID
   * @param {number} csrId - CSR ID
   * @returns {Promise<Object>} CSR details
   */
  getCSRById: async (csrId) => {
    const response = await api.get(`/certificates/csrs/${csrId}`);
    return response.data;
  },

  /**
   * List CSRs with optional status filter
   * @param {string} status - Status filter (PENDING, SIGNED, REJECTED, EXPIRED)
   * @returns {Promise<Array>} List of CSRs
   */
  listCSRs: async (status = null) => {
    const params = status ? { status } : {};
    const response = await api.get('/certificates/csrs', { params });
    return response.data;
  }
};

/**
 * User Management API Service
 */
export const userApi = {
  /**
   * Get all users with optional filtering
   * @param {Object} filters - Filter parameters
   * @param {string} filters.status - Status filter (ACTIVE, INACTIVE, LOCKED, PENDING)
   * @param {string} filters.role - Role filter (ADMIN, OPERATOR, VIEWER)
   * @param {string} filters.search - Search term
   * @returns {Promise<Array>} List of users
   */
  getUsers: async (filters = {}) => {
    const params = {};

    if (filters.status) params.status = filters.status;
    if (filters.role) params.role = filters.role;
    if (filters.search) params.search = filters.search;

    const response = await api.get('/users', { params });
    return response.data;
  },

  /**
   * Get user by ID
   * @param {number} id - User ID
   * @returns {Promise<Object>} User details
   */
  getUserById: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response.data.user;
  },

  /**
   * Create new user
   * @param {Object} userData - User data
   * @param {string} userData.username - Username
   * @param {string} userData.email - Email
   * @param {string} userData.fullName - Full name
   * @param {string} userData.password - Password
   * @param {string} userData.role - Role (ADMIN, OPERATOR, VIEWER)
   * @param {string} userData.notes - Optional notes
   * @returns {Promise<Object>} Created user
   */
  createUser: async (userData) => {
    const response = await api.post('/users', userData);
    return response.data;
  },

  /**
   * Update user
   * @param {number} id - User ID
   * @param {Object} updates - User updates
   * @returns {Promise<Object>} Updated user
   */
  updateUser: async (id, updates) => {
    const response = await api.put(`/users/${id}`, updates);
    return response.data;
  },

  /**
   * Delete (deactivate) user
   * @param {number} id - User ID
   * @returns {Promise<void>}
   */
  deleteUser: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },

  /**
   * Activate user
   * @param {number} id - User ID
   * @returns {Promise<void>}
   */
  activateUser: async (id) => {
    const response = await api.post(`/users/${id}/activate`);
    return response.data;
  },

  /**
   * Deactivate user
   * @param {number} id - User ID
   * @returns {Promise<void>}
   */
  deactivateUser: async (id) => {
    const response = await api.post(`/users/${id}/deactivate`);
    return response.data;
  },

  /**
   * Unlock user account
   * @param {number} id - User ID
   * @returns {Promise<void>}
   */
  unlockUser: async (id) => {
    const response = await api.post(`/users/${id}/unlock`);
    return response.data;
  },

  /**
   * Reset user password
   * @param {number} id - User ID
   * @returns {Promise<Object>} Temporary password
   */
  resetPassword: async (id) => {
    const response = await api.post(`/users/${id}/reset-password`);
    return response.data;
  },

  /**
   * Change user password
   * @param {number} id - User ID
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<void>}
   */
  changePassword: async (id, currentPassword, newPassword) => {
    const response = await api.post(`/users/${id}/change-password`, {
      currentPassword,
      newPassword
    });
    return response.data;
  },

  /**
   * Get user statistics
   * @returns {Promise<Object>} User statistics
   */
  getUserStats: async () => {
    const response = await api.get('/users/stats');
    return response.data;
  },

  /**
   * Login
   * @param {string} username - Username
   * @param {string} password - Password
   * @returns {Promise<Object>} Login result with token and user
   */
  login: async (username, password, tenantId) => {
    const body = { username, password };
    // tenantId is optional — only sent in MULTI_TENANT mode
    if (tenantId && tenantId.trim()) body.tenantId = tenantId.trim();
    const response = await api.post('/auth/login', body);
    return response.data;
  }
};

/**
 * Application Event Log API Service
 */
export const applicationEventApi = {
  /**
   * Get filtered event list (newest first)
   * @param {Object} filters
   * @param {number}  [filters.limit=200]
   * @param {string}  [filters.level]          INFO | WARN | ERROR
   * @param {string}  [filters.category]       SIGNING | VERIFICATION | SWITCH_COMM | AUTH | SYSTEM
   * @param {string}  [filters.correlationId]  Partial match on correlation ID
   * @param {string}  [filters.search]         Free-text match on message or correlation ID
   * @param {string}  [filters.from]           ISO-8601 datetime
   * @param {string}  [filters.to]             ISO-8601 datetime
   * @returns {Promise<Array>} List of application events
   */
  getEvents: async (filters = {}) => {
    const params = {};
    if (filters.limit)         params.limit         = filters.limit;
    if (filters.level)         params.level         = filters.level;
    if (filters.category)      params.category      = filters.category;
    if (filters.correlationId) params.correlationId = filters.correlationId;
    if (filters.search)        params.search        = filters.search;
    if (filters.from)          params.from          = filters.from;
    if (filters.to)            params.to            = filters.to;

    const response = await api.get('/admin/events', { params });
    return response.data;
  },

  /**
   * Get a single event by ID
   * @param {number} id - Event ID
   * @returns {Promise<Object>} Event details
   */
  getEventById: async (id) => {
    const response = await api.get(`/admin/events/${id}`);
    return response.data;
  },

  /**
   * Get stats banner counts
   * @returns {Promise<Object>} { totalErrors, totalWarns, errorsLast24h, authRejections, switchFailures }
   */
  getEventStats: async () => {
    const response = await api.get('/admin/events/stats');
    return response.data;
  },

  /**
   * Get the 10 most recent ERROR events (for Dashboard widget)
   * @returns {Promise<Array>} Recent error events
   */
  getRecentErrors: async () => {
    const response = await api.get('/admin/events/recent-errors');
    return response.data;
  }
};

// =========================================================================
// Performance Test API Service  [DEV ONLY — only active when PERF_TEST_ENABLED=true]
// =========================================================================

export const perfTestApi = {
  /**
   * Fetch the perf test configuration (also used to check if feature is enabled).
   * Returns { enabled, maxConcurrency, maxIterations, simulatorUrl }
   * Returns { enabled: false } when the feature flag is off (404 from server).
   */
  getConfig: async () => {
    try {
      const response = await api.get('/perf/config');
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return { enabled: false };
      }
      throw error;
    }
  },

  /**
   * Start a new performance test run.
   * @param {Object} params
   * @param {'SIGN'|'VERIFY'|'FORWARD'} params.operation
   * @param {number} params.concurrency
   * @param {number} params.iterations
   * @param {number} params.warmupIterations
   * @returns {Promise<{ runId: string, status: string, message: string }>}
   */
  startRun: async ({ operation, concurrency, iterations, warmupIterations }) => {
    const response = await api.post('/perf/run', {
      operation,
      concurrency,
      iterations,
      warmupIterations,
    });
    return response.data;
  },

  /**
   * Poll for the status/result of a specific run.
   * @param {string} runId
   * @returns {Promise<PerformanceTestResultDto>}
   */
  getResult: async (runId) => {
    const response = await api.get(`/perf/results/${runId}`);
    return response.data;
  },

  /**
   * List recent test results.
   * @param {number} limit  Max number of results (1–100, default 20)
   * @returns {Promise<PerformanceTestResultDto[]>}
   */
  listResults: async (limit = 20) => {
    const response = await api.get('/perf/results', { params: { limit } });
    return response.data;
  },
};

// =========================================================================
// Audit Export API Service  [ADMIN ONLY]
// Compliance audit log export — CSV and PDF
// =========================================================================

export const auditExportApi = {
  /**
   * Fetch a preview (summary counts + record list, no file download).
   * Use this to populate counts and the preview table before downloading.
   *
   * @param {Object} params
   * @param {string} [params.from]        ISO date "YYYY-MM-DD"
   * @param {string} [params.to]          ISO date "YYYY-MM-DD"
   * @param {string} [params.direction]   "ALL" | "INCOMING" | "OUTGOING"
   * @param {string} [params.search]      Free-text (msgId, e2eId, accountId…)
   * @param {string} [params.reportTitle] Optional report title override
   * @returns {Promise<Object>}  AuditReportDto with payments, registrations, etc.
   */
  preview: async (params = {}) => {
    const response = await api.get('/admin/audit-export/preview', { params });
    return response.data;
  },

  /**
   * Trigger a CSV download (streams through axios so the API key header is sent).
   *
   * @param {Object} params     Same shape as preview()
   * @param {string} filename   Suggested filename (default: audit_report.csv)
   */
  downloadCsv: async (params = {}, filename = 'audit_report.csv') => {
    const response = await api.get('/admin/audit-export/csv', {
      params,
      responseType: 'blob',
      headers: { Accept: 'text/csv' },
    });
    const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
    const a   = document.createElement('a');
    a.href     = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  /**
   * Trigger a PDF download (streams through axios so the API key header is sent).
   *
   * @param {Object} params     Same shape as preview()
   * @param {string} filename   Suggested filename (default: audit_report.pdf)
   */
  downloadPdf: async (params = {}, filename = 'audit_report.pdf') => {
    const response = await api.get('/admin/audit-export/pdf', {
      params,
      responseType: 'blob',
      headers: { Accept: 'application/pdf' },
    });
    const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a   = document.createElement('a');
    a.href     = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};

// =========================================================================
// System / Protocol API Service
// =========================================================================

/**
 * Returns the active switch protocol configuration.
 *
 * Response shape:
 * {
 *   "protocol": "ISO20022_XMLDSIG" | "MOJALOOP_FSPIOP_V1_1",
 *   "label":    "ISO 20022 / XMLDSig" | "Mojaloop FSPIOP v1.1",
 *   "version":  "v1.1" | ""
 * }
 */
export const systemApi = {
  getProtocol: async () => {
    const response = await api.get('/admin/system/protocol');
    return response.data;
  },

  /**
   * Mojaloop FSPIOP dashboard statistics.
   *
   * Response shape:
   * {
   *   partyLookups:  { total, resolved, notFound, pending, error },
   *   quotes:        { total, received, pending, expired, failed },
   *   transfers:     { total, committed, reserved, aborted, expired, pending },
   *   fspKeys:       { total, active },
   *   recentTransfers: [ { transferId, payerFsp, payeeFsp, amount, currency, status, createdAt } ]
   * }
   */
  getMojaloopStats: async () => {
    const response = await api.get('/admin/system/mojaloop/stats');
    console.log('[API] getMojaloopStats response:', JSON.stringify(response.data, null, 2));
    return response.data;
  },

  getIdentity: async () => {
    const response = await api.get('/admin/identity');
    return response.data; // { fspId, displayName }
  },
};

// =========================================================================
// Mojaloop FSPIOP API
// =========================================================================

/**
 * Mojaloop entity APIs — party lookups, quotes, transfers, FSP keys.
 */
export const mojaloopApi = {

  // ── Party Lookups ──────────────────────────────────────────────────────

  /** @param {{ status?: string, search?: string, page?: number, size?: number }} params */
  getPartyLookups: async ({ status, search, page = 0, size = 20 } = {}) => {
    const params = { page, size };
    if (status) params.status = status;
    if (search) params.search = search;
    const response = await api.get('/admin/party-lookups', { params });
    console.log('[API] getPartyLookups response:', JSON.stringify(response.data, null, 2));
    return response.data; // { content, page, size, totalItems, totalPages }
  },

  getPartyLookupById: async (id) => {
    const response = await api.get(`/admin/party-lookups/${id}`);
    return response.data;
  },

  // ── Quotes ─────────────────────────────────────────────────────────────

  /** @param {{ status?: string, search?: string, page?: number, size?: number }} params */
  getQuotes: async ({ status, search, page = 0, size = 20 } = {}) => {
    const params = { page, size };
    if (status) params.status = status;
    if (search) params.search = search;
    const response = await api.get('/admin/quotes', { params });
    console.log('[API] getQuotes response:', JSON.stringify(response.data, null, 2));
    return response.data; // { content, page, size, totalItems, totalPages }
  },

  getQuoteById: async (id) => {
    const response = await api.get(`/admin/quotes/${id}`);
    return response.data;
  },

  // ── Transfers ──────────────────────────────────────────────────────────

  /** @param {{ status?: string, search?: string, page?: number, size?: number }} params */
  getTransfers: async ({ status, search, page = 0, size = 20 } = {}) => {
    const params = { page, size };
    if (status) params.status = status;
    if (search) params.search = search;
    const response = await api.get('/admin/transfers', { params });
    console.log('[API] getTransfers response:', JSON.stringify(response.data, null, 2));
    return response.data; // { content, page, size, totalItems, totalPages }
  },

  getTransferById: async (id) => {
    const response = await api.get(`/admin/transfers/${id}`);
    return response.data;
  },

  // ── FSP Keys ───────────────────────────────────────────────────────────

  /**
   * List FSP keys.
   * @param {{ activeOnly?: boolean, hubOnly?: boolean }} filters
   */
  getFspKeys: async ({ activeOnly, hubOnly } = {}) => {
    const params = {};
    if (activeOnly) params.activeOnly = true;
    if (hubOnly)    params.hubOnly    = true;
    const response = await api.get('/admin/fsp-keys', { params });
    return response.data;
  },

  getFspKeyById: async (id) => {
    const response = await api.get(`/admin/fsp-keys/${id}`);
    return response.data;
  },

  /**
   * Import a new FSP public key (Hub or participant).
   * The server validates the PEM, extracts fingerprint / key size / expiry,
   * and records the authenticated admin username in imported_by.
   *
   * @param {{
   *   fspId:          string,
   *   publicKeyPem:   string,
   *   isHubKey:       boolean,
   *   description?:   string,
   *   rotationNotes?: string
   * }} request
   */
  importFspKey: async (request) => {
    const response = await api.post('/admin/fsp-keys/import', request);
    return response.data;
  },

  /**
   * Rotate an FSP key: atomically deactivates the key with the given ID and
   * activates the replacement, chaining them via previous_key_id.
   *
   * @param {number} id              ID of the key being replaced
   * @param {{
   *   publicKeyPem:   string,
   *   rotationNotes?: string
   * }} request
   */
  rotateFspKey: async (id, request) => {
    const response = await api.post(`/admin/fsp-keys/${id}/rotate`, request);
    return response.data;
  },

  /**
   * Soft-deactivate an FSP key. The row is retained for audit history
   * and rotation chain traversal.
   * @param {number} id
   */
  deactivateFspKey: async (id) => {
    const response = await api.delete(`/admin/fsp-keys/${id}`);
    return response.data;
  },

  // ── Payee Requests (inbound Hub requests — payee flow) ─────────────────

  /**
   * List inbound Hub requests with optional filtering and pagination.
   *
   * @param {{ status?: string, requestType?: string, search?: string, page?: number, size?: number }} params
   * @returns {Promise<PageResponse>}  { content, page, size, totalItems, totalPages }
   */
  getPayeeRequests: async ({ status, requestType, search, page = 0, size = 20 } = {}) => {
    const params = { page, size };
    if (status)      params.status      = status;
    if (requestType) params.requestType = requestType;
    if (search)      params.search      = search;
    const response = await api.get('/admin/payee-requests', { params });
    return response.data;
  },

  getPayeeRequestById: async (id) => {
    const response = await api.get(`/admin/payee-requests/${id}`);
    return response.data;
  },
};

// =========================================================================
// Platform Admin API  [MULTI_TENANT mode only]
// Separate axios instance using X-Platform-Key header.
// Key stored in sessionStorage (cleared on tab close).
// =========================================================================

// The operator's console session (issued by /api/platform/auth/redeem) is what
// authenticates every console request, on BOTH engines: they share the signing
// secret. A platform key in sessionStorage is still honoured as break-glass —
// there is no UI that sets one any more, but automation and a locked-out
// console can.
const OPERATOR_SESSION  = 'platform_session';
const OPERATOR_IDENTITY = 'platform_operator';
const LEGACY_KEY        = 'platform_key';

function attachOperatorAuth(config) {
  const session = sessionStorage.getItem(OPERATOR_SESSION);
  const key     = sessionStorage.getItem(LEGACY_KEY);
  if (session)  config.headers['Authorization'] = `Bearer ${session}`;
  else if (key) config.headers['X-Platform-Key'] = key;
  return config;
}

function clearOperatorAuth() {
  sessionStorage.removeItem(OPERATOR_SESSION);
  sessionStorage.removeItem(OPERATOR_IDENTITY);
  sessionStorage.removeItem(LEGACY_KEY);
}

const platformAxios = (() => {
  const instance = axios.create({ baseURL: '/api/platform' });
  instance.interceptors.request.use(attachOperatorAuth);
  instance.interceptors.response.use(
    r => r,
    err => {
      // A 401 means the session expired or the operator was revoked; either way
      // the person has to sign in again. The sign-in call itself is excluded so
      // a refused login shows its reason instead of bouncing.
      if (err.response?.status === 401 && !String(err.config?.url || '').includes('/auth/redeem')) {
        clearOperatorAuth();
        window.location.href = '/platform/login?error=session_expired';
      }
      return Promise.reject(err);
    }
  );
  return instance;
})();

// An engine's API root, with the console session: '/iso20022/api' or '/iso8583/api'.
const engineAxios = (engine) => {
  const instance = axios.create({ baseURL: `/${engine}/api` });
  instance.interceptors.request.use(attachOperatorAuth);
  return instance;
};

// Operator endpoints on the ISO engine (proxied at /iso20022/api), accepting the
// same console session.
const operatorAxios = (() => {
  const instance = axios.create({ baseURL: '/iso20022/api/operator' });
  instance.interceptors.request.use(attachOperatorAuth);
  return instance;
})();

export const operatorApi = {
  /** The scheme profiles this Sandbox serves, summarised, and any profile files refused: { profiles, problems }. */
  schemeProfileCatalogue: async () => (await operatorAxios.get('/scheme-profiles')).data,
  /** A tenant's scheme profile assignments, each with its profile and why the engine cannot serve it (or null). */
  tenantSchemeProfiles: async (tenantId) =>
    (await operatorAxios.get(`/scheme-profiles/tenants/${encodeURIComponent(tenantId)}`)).data,

  /** Per-participant certification + usage overview (all tenants). */
  overview:       async () => (await operatorAxios.get('/overview')).data,
  /** Certification + usage drill-down for one participant. */
  tenantOverview: async (tenantId) => (await operatorAxios.get(`/overview/${tenantId}`)).data,

  /**
   * Every participant with an ISO 20022 run finalized and awaiting a decision.
   * Cross-tenant, so the console can surface pending work without the officer
   * opening each tenant in turn.
   */
  pendingApprovals: async () =>
    (await operatorAxios.get('/certification/pending')).data,

  /**
   * Whether this participant may launch automated ISO 20022 certification runs.
   * The flag lives in the ISO engine's own users table, so it is read and written
   * here rather than alongside the FSPIOP one on `platformApi`.
   */
  autoRunAccess: async (tenantId) =>
    (await operatorAxios.get(`/certification/${tenantId}/auto-run-access`)).data,

  /** Grant or withdraw automated ISO 20022 certification runs for this participant. */
  setAutoRunAccess: async (tenantId, enabled) =>
    (await operatorAxios.put(`/certification/${tenantId}/auto-run-access`, { enabled })).data,

  /** The tenant's latest finalized certification report (for officer review). */
  certReport: async (tenantId) =>
    (await operatorAxios.get(`/certification/${tenantId}/report`)).data,

  /** What each ISO 20022 category and case covers, and how a participant passes a case. */
  certCatalogue: async () => (await operatorAxios.get('/certification/catalogue')).data,

  /**
   * Record the CoE authorising-officer decision on the latest finalized report.
   * Final once recorded; 409 when a newer report was finalized or this one is decided.
   * @param {Object} body - { reportId, resultHash (the report reviewed),
   *                          decision: 'APPROVED'|'APPROVED_WITH_CONDITIONS'|'REJECTED',
   *                          authorisingOfficer, title?, notes? } — see signOffBody
   */
  certSignOff: async (tenantId, body) =>
    (await operatorAxios.post(`/certification/${tenantId}/sign-off`, body)).data,

  /** Revoke the certificate issued from an approved report: { reportId, resultHash, reason, ... }. */
  certRevoke: async (tenantId, body) =>
    (await operatorAxios.post(`/certification/${tenantId}/revoke`, body)).data,

  /** Every decision and revocation recorded on a report, oldest first. */
  certDecisions: async (tenantId, reportId) =>
    (await operatorAxios.get(`/certification/${tenantId}/decisions`, { params: { reportId } })).data,

  /** Download the issued certificate PDF (approved runs only). */
  certCertificate: async (tenantId) => {
    const r = await operatorAxios.get(`/certification/${tenantId}/certificate`, { responseType: 'blob' });
    const m = (r.headers['content-disposition'] || '').match(/filename="?([^"]+)"?/);
    return { blob: r.data, filename: m ? m[1] : `${tenantId}-certificate.pdf` };
  },
};

// Operator endpoints on the ISO 8583 card engine (proxied at /iso8583/api),
// accepting the same console session. The engine embeds the shared tenancy
// library, so it also serves /api/platform — which is where a tenant's card
// institution ids are managed, on the engine that uses them.
const cardOperatorAxios = (() => {
  const instance = axios.create({ baseURL: '/iso8583/api' });
  instance.interceptors.request.use(attachOperatorAuth);
  return instance;
})();

export const cardOperatorApi = {
  /** Card-corridor runs finalized and awaiting a decision (all tenants on ISO8583). */
  pendingApprovals: async () =>
    (await cardOperatorAxios.get('/operator/certification/pending')).data,
  /** A tenant's latest finalized card-corridor report; 404 when there is none. */
  certReport: async (tenantId) =>
    (await cardOperatorAxios.get(`/operator/certification/${tenantId}/report`)).data,
  /** What each card category and case covers, and the steps on the link that pass a case. */
  certCatalogue: async () => (await cardOperatorAxios.get('/operator/certification/catalogue')).data,
  /** The authorising officer's decision on that report, naming it by id and hash (see signOffBody). */
  certSignOff: async (tenantId, body) =>
    (await cardOperatorAxios.post(`/operator/certification/${tenantId}/sign-off`, body)).data,
  /** Revoke the certificate issued from an approved card-corridor report. */
  certRevoke: async (tenantId, body) =>
    (await cardOperatorAxios.post(`/operator/certification/${tenantId}/revoke`, body)).data,
  /** Every decision and revocation recorded on a card-corridor report, oldest first. */
  certDecisions: async (tenantId, reportId) =>
    (await cardOperatorAxios.get(`/operator/certification/${tenantId}/decisions`, { params: { reportId } })).data,
  /** The certificate PDF for an approved card-corridor run. */
  certCertificate: async (tenantId) => {
    const r = await cardOperatorAxios.get(`/operator/certification/${tenantId}/certificate`, { responseType: 'blob' });
    const m = (r.headers['content-disposition'] || '').match(/filename="?([^"]+)"?/);
    return { blob: r.data, filename: m ? m[1] : `${tenantId}-card-certificate.pdf` };
  },
  /** The dialect the card link speaks, the deployment's default, and every profile it could switch to. */
  dialect: async () => (await cardOperatorAxios.get('/operator/dialect')).data,
  /** Moves the card link to another catalogued profile, live. Drops every open link. */
  switchDialect: async (id) => (await cardOperatorAxios.post('/operator/dialect', { id })).data,
  /** The institution ids a tenant signs on with (DE 32). */
  cardInstitutions: async (tenantId) =>
    (await cardOperatorAxios.get(`/platform/tenants/${tenantId}/card-institutions`)).data,
  /** Replaces them — each 1 to 11 digits, belonging to no other tenant. */
  setCardInstitutions: async (tenantId, institutionIds) =>
    (await cardOperatorAxios.put(`/platform/tenants/${tenantId}/card-institutions`, { institutionIds })).data,
};

// Public self-service onboarding (WP-8) — unauthenticated; uses bare axios so no
// Bearer/tenant/platform headers are attached.
export const onboardingApi = {
  availability: async (tenantId, fspId) =>
    (await axios.get('/api/onboarding/availability', { params: { tenantId, fspId } })).data,
  submit:       async (payload) => (await axios.post('/api/onboarding/applications', payload)).data,
  verifyEmail:  async (token) => (await axios.post('/api/onboarding/verify-email', null, { params: { token } })).data,
  status:       async (statusToken) => (await axios.get(`/api/onboarding/applications/${statusToken}`)).data,
  /** Sends a revised application back for review, after AfricaNenda asked for changes. */
  resubmit:     async (statusToken, payload) =>
    (await axios.post('/api/onboarding/resubmit', payload, { params: { statusToken } })).data,
  activate:     async (token, password) =>
    (await axios.post('/api/onboarding/activate', { password }, { params: { token } })).data,
};

// The public check behind a printed certificate's QR code — unauthenticated, so
// bare axios. Each engine answers for its own corridor, named in the report id
// (CERT-<FSP>-<CORRIDOR>-<DATE>-<NNN>); the portal and the edge already route
// all three prefixes.
const CERTIFICATE_ENGINES = { FSPIOP: '/api', ISO20022: '/iso20022/api', ISO8583: '/iso8583/api' };

export const certificateCheckApi = {
  /** Resolves to the engine's verdict on {reportId, h}; null when the id names no corridor this Sandbox has. */
  verify: async (reportId, hashFragment) => {
    const corridor = (reportId || '').split('-')[2];
    const base = CERTIFICATE_ENGINES[corridor];
    if (!base) return null;
    return (await axios.get(`${base}/public/certificates/${encodeURIComponent(reportId)}`,
      { params: { h: hashFragment } })).data;
  },
};

export const platformApi = {
  // ── Sign-in ──────────────────────────────────────────────────────────────
  /** What the login page needs: which email domains may sign in, and whether mail really goes out. */
  authConfig: async () => (await axios.get('/api/platform/auth/config')).data,

  /**
   * Asks for a one-time sign-in link to be emailed. Answers the same whether or
   * not the address is an operator, so it cannot be used to enumerate them.
   */
  requestLink: async (email) => (await axios.post('/api/platform/auth/request-link', { email })).data,

  /** Redeems the link's token: the whole sign-in. Stores the console session it returns. */
  redeemLink: async (token) => {
    const r = await axios.post('/api/platform/auth/redeem', { token });
    sessionStorage.setItem(OPERATOR_SESSION, r.data.token);
    sessionStorage.setItem(OPERATOR_IDENTITY, JSON.stringify(r.data.operator || {}));
    return r.data;
  },

  /** Who the current session is, as the engine sees it. */
  me: async () => (await platformAxios.get('/auth/me')).data,

  /** The signed-in operator as stored at login (no request), or null. */
  currentOperator: () => {
    try {
      const raw = sessionStorage.getItem(OPERATOR_IDENTITY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  },

  /**
   * Super admin: validate the platform key and use it for this tab. This is
   * the credential that confirms access requests, invites and revokes; every
   * action taken with it is audited under 'super-admin'.
   */
  login: async (platformKey) => {
    const r = await axios.get('/api/platform/health', {
      headers: { 'X-Platform-Key': platformKey }
    });
    sessionStorage.setItem(LEGACY_KEY, platformKey);
    return r.data;
  },

  logout: () => { clearOperatorAuth(); },

  isAuthenticated: () =>
    !!sessionStorage.getItem(OPERATOR_SESSION) || !!sessionStorage.getItem(LEGACY_KEY),

  /** This tab is the super admin: signed in with the platform key, not an operator session. */
  isSuperAdmin: () =>
    !sessionStorage.getItem(OPERATOR_SESSION) && !!sessionStorage.getItem(LEGACY_KEY),

  // ── Operators & audit ────────────────────────────────────────────────────
  listOperators:  async () => (await platformAxios.get('/operators')).data,
  inviteOperator: async (email, displayName) =>
    (await platformAxios.post('/operators/invite', { email, displayName })).data,
  resendOperatorLink: async (email) =>
    (await platformAxios.post(`/operators/${encodeURIComponent(email)}/resend`)).data,
  /** Access requests awaiting the super admin's one-time confirmation. */
  listOperatorRequests: async () => (await platformAxios.get('/operators/requests')).data,
  approveOperator: async (email, displayName) =>
    (await platformAxios.post(`/operators/${encodeURIComponent(email)}/approve`, { displayName })).data,
  rejectOperator: async (email) =>
    (await platformAxios.post(`/operators/${encodeURIComponent(email)}/reject`)).data,
  revokeOperator: async (email) =>
    (await platformAxios.delete(`/operators/${encodeURIComponent(email)}`)).data,
  auditTrail:     async (limit = 100) => (await platformAxios.get('/audit', { params: { limit } })).data,

  // ── Tenants ─────────────────────────────────────────────────────────────
  /**
   * Whether this participant may launch automated FSPIOP certification runs.
   * Served by the FSPIOP engine under /api/platform, so it belongs here rather
   * than on operatorApi, which targets the ISO engine.
   */
  /**
   * Every participant with a FSPIOP run finalized and awaiting a decision.
   * Served by the FSPIOP engine, so the console asks both corridors and merges.
   */
  fspiopPendingApprovals: async () =>
    (await platformAxios.get('/certification/pending')).data,

  fspiopAutoRunAccess: async (tenantId) =>
    (await platformAxios.get(`/certification/${tenantId}/auto-run-access`)).data,

  /** Grant or withdraw automated FSPIOP certification runs for this participant. */
  setFspiopAutoRunAccess: async (tenantId, enabled) =>
    (await platformAxios.put(`/certification/${tenantId}/auto-run-access`, { enabled })).data,

  /**
   * FSPIOP corridor certification, for officer review.
   *
   * The ISO corridor's equivalents live on `operatorApi`, which targets the ISO
   * engine; these are served by the FSPIOP engine, so a FSPIOP run only ever
   * appears for approval through here.
   */
  fspiopCertReport: async (tenantId) =>
    (await platformAxios.get(`/certification/${tenantId}/report`)).data,

  /** What each FSPIOP category and case covers, and how a participant passes a case. */
  fspiopCertCatalogue: async () => (await platformAxios.get('/certification/catalogue')).data,

  /** Record the CoE authorising-officer decision on the latest finalized FSPIOP report (see signOffBody). */
  fspiopCertSignOff: async (tenantId, body) =>
    (await platformAxios.post(`/certification/${tenantId}/sign-off`, body)).data,

  /** Revoke the certificate issued from an approved FSPIOP report. */
  fspiopCertRevoke: async (tenantId, body) =>
    (await platformAxios.post(`/certification/${tenantId}/revoke`, body)).data,

  /** Every decision and revocation recorded on a FSPIOP report, oldest first. */
  fspiopCertDecisions: async (tenantId, reportId) =>
    (await platformAxios.get(`/certification/${tenantId}/decisions`, { params: { reportId } })).data,

  /** The issued FSPIOP certificate PDF (approved runs only). */
  fspiopCertCertificate: async (tenantId) => {
    const r = await platformAxios.get(`/certification/${tenantId}/certificate`, { responseType: 'blob' });
    const m = (r.headers['content-disposition'] || '').match(/filename="?([^"]+)"?/);
    return { blob: r.data, filename: m ? m[1] : `${tenantId}-fspiop-certificate.pdf` };
  },

  listTenants: async () => (await platformAxios.get('/tenants')).data,
  getTenant:   async (id) => (await platformAxios.get(`/tenants/${id}`)).data,
  createTenant: async (payload) => (await platformAxios.post('/tenants', payload)).data,
  /** Change a tenant's status. A reason is required to suspend, optional to activate. */
  updateStatus: async (tenantId, status, reason) =>
    (await platformAxios.patch(`/tenants/${tenantId}/status`, { status, reason })).data,

  updateCorridors: async (tenantId, corridors) =>
    (await platformAxios.patch(`/tenants/${tenantId}/corridors`, { corridors })).data,

  // ── Scheme profiles (ISO 20022 cross-border) ─────────────────────────────
  // Stored by the platform; operatorApi.tenantSchemeProfiles reads them back with
  // the ISO engine's verdict on each, which only that engine can give.
  /** Assign the tenant to a scheme profile, or change it: { memberId, homeCountry, homeCurrency, roles }. */
  assignSchemeProfile: async (tenantId, profileId, assignment) =>
    (await platformAxios.put(`/tenants/${tenantId}/scheme-profiles/${encodeURIComponent(profileId)}`, assignment)).data,
  /** Remove the tenant from a scheme profile. */
  removeSchemeProfile: async (tenantId, profileId) =>
    (await platformAxios.delete(`/tenants/${tenantId}/scheme-profiles/${encodeURIComponent(profileId)}`)).data,
  /**
   * Decommission (hard-delete) a tenant on the FSPIOP engine: its schema there, the
   * tenant record, API keys and applications. The reason is required and kept in
   * the audit trail. Call removeEngineData on the other engines first.
   */
  deleteTenant: async (tenantId, reason) =>
    (await platformAxios.delete(`/tenants/${tenantId}`, { data: { reason } })).data,
  /**
   * Removes one engine's own data for a tenant being decommissioned: the schema it
   * keeps for it. Each engine's database account may drop only its own schemas, so
   * the console asks the ISO 20022 ('iso20022') and card ('iso8583') engines
   * before deleteTenant removes the tenant record.
   */
  removeEngineData: async (engine, tenantId, reason) =>
    (await engineAxios(engine).delete(`/platform/tenants/${tenantId}/corridor-data`, { data: { reason } })).data,

  // ── API Keys ─────────────────────────────────────────────────────────────
  listKeys:    async (tenantId) => (await platformAxios.get(`/tenants/${tenantId}/keys`)).data,
  provisionKey: async (tenantId, label, expiresAt) =>
    (await platformAxios.post(`/tenants/${tenantId}/keys`, { label, expiresAt })).data,
  revokeKey: async (tenantId, keyId) =>
    (await platformAxios.delete(`/tenants/${tenantId}/keys/${keyId}`)).data,

  // A participant's Admin-role accounts; a reset names one of them.
  listTenantAdmins: async (tenantId) =>
    (await platformAxios.get(`/tenants/${tenantId}/admins`)).data,
  resetAdminPassword: async (tenantId, username) =>
    (await platformAxios.post(`/tenants/${tenantId}/reset-admin-password`, { username })).data,

  // ── Onboarding applications (WP-8) ─────────────────────────────────────────
  listApplications:   async (status) =>
    (await platformAxios.get('/applications', { params: status ? { status } : {} })).data,
  getApplication:     async (id) => (await platformAxios.get(`/applications/${id}`)).data,
  startReview:        async (id) => (await platformAxios.post(`/applications/${id}/start-review`)).data,
  resendVerification: async (id) => (await platformAxios.post(`/applications/${id}/resend-verification`)).data,
  approveApplication: async (id, payload) =>
    (await platformAxios.post(`/applications/${id}/approve`, payload || {})).data,
  rejectApplication:  async (id, note) =>
    (await platformAxios.post(`/applications/${id}/reject`, { note })).data,
  requestChanges:     async (id, note) =>
    (await platformAxios.post(`/applications/${id}/request-changes`, { note })).data,

  // ── Mojaloop Hub link ────────────────────────────────────────────────────
  /**
   * The FSPIOP engine's Hub addresses, whether it targets a Hub, and the
   * numbers and amount a check run sends. Contacts nothing.
   */
  hubStatus: async () => (await platformAxios.get('/hub/status')).data,

  /**
   * Starts a run of the Hub link checks as a tenant and returns the run at
   * once (state RUNNING). Answers 409 with the running run's id while another
   * run is in progress; one run at a time across the engine.
   */
  startHubRun: async (tenantId, includeTransfer) =>
    (await platformAxios.post('/hub/runs', { tenantId, includeTransfer: !!includeTransfer })).data,

  /** One run, in progress or finished. */
  hubRun: async (runId) => (await platformAxios.get(`/hub/runs/${encodeURIComponent(runId)}`)).data,

  /** The most recent runs, newest first, the one in progress included. */
  hubRuns: async (limit = 20) => (await platformAxios.get('/hub/runs', { params: { limit } })).data,

  /**
   * One page of the same runs, newest first, for the history table:
   * { content, page, size, totalElements, totalPages, first, last, hasNext, hasPrevious }.
   * A run in progress is at the top of the first page.
   */
  hubHistory: async (page = 0, size = 10) =>
    (await platformAxios.get('/hub/history', { params: { page, size } })).data,

  // ── CBS Webhook Configuration ────────────────────────────────────────────
  /**
   * Read the per-tenant CBS webhook URL configuration.
   *
   * Response shape:
   * {
   *   cbsUrlPartyLookup:   string | null,  // null = using global CBS_PAYEE_WEBHOOK_URL fallback
   *   cbsUrlQuote:         string | null,
   *   cbsUrlTransfer:      string | null,
   *   cbsUrlTransferError: string | null,
   *   cbsWebhookApiKey:    string | null,  // null = no auth / global fallback
   * }
   */
  getCbsConfig: async (tenantId) =>
    (await platformAxios.get(`/tenants/${tenantId}/cbs-config`)).data,

  /**
   * Save the per-tenant CBS webhook URL configuration.
   * Blank strings are treated as null (reverts to global fallback).
   * Changes take effect immediately — no middleware restart needed.
   *
   * @param {string} tenantId
   * @param {{
   *   cbsUrlPartyLookup?:   string,
   *   cbsUrlQuote?:         string,
   *   cbsUrlTransfer?:      string,
   *   cbsUrlTransferError?: string,
   *   cbsWebhookApiKey?:    string,
   * }} payload
   */
  saveCbsConfig: async (tenantId, payload) =>
    (await platformAxios.put(`/tenants/${tenantId}/cbs-config`, payload)).data,
};

/**
 * FSPIOP corridor certification — the participant's own view of their run.
 *
 * Served by the FSPIOP engine, so it lives on this instance rather than the ISO
 * one, whose `certificationApi` covers the other corridor.
 */
export const fspiopCertificationApi = {
  /** The live run: every case with its current verdict, NOT_RUN until exercised. */
  progress: async () => (await api.get('/certification/progress')).data,

  /** The last finalized report — what the officer reviews, and what a run leaves behind. */
  latestReport: async () => (await api.get('/certification/report/latest')).data,

  /** Freeze the active run and issue its report. */
  finalize: async () => (await api.post('/certification/finalize')).data,

  /** Start an automated run of the whole collection. 403 unless the account is enabled for it. */
  startAutoRun: async () => (await api.post('/certification/auto-run')).data,

  /** Live progress of an automated run. */
  autoRunStatus: async () => (await api.get('/certification/auto-run/status')).data,

  /** The certification pack (instructions and samples) as a zip. */
  downloadInstructions: async () => {
    // The pack names the API base the participant calls: this Sandbox's, not a local port.
    const r = await api.get('/certification/instructions', {
      responseType: 'blob', params: { base: participantApiBase('FSPIOP') },
    });
    const m = (r.headers['content-disposition'] || '').match(/filename="?([^"]+)"?/);
    return { blob: r.data, filename: m ? m[1] : 'fspiop-certification-pack.zip' };
  },

  /** The certificate PDF. 409 until the CoE authorising officer has approved the run. */
  downloadCertificate: async () => {
    const r = await api.get('/certification/certificate', { responseType: 'blob' });
    const m = (r.headers['content-disposition'] || '').match(/filename="?([^"]+)"?/);
    return { blob: r.data, filename: m ? m[1] : 'fspiop-certificate.pdf' };
  },
};

export default api;
